import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { SavedBrandCollection } from "schemas/dashboard";

const ALL_SAVED_ID = 0;

type Mode = "keep" | "remove";

interface Props {
  open: boolean;
  collection: SavedBrandCollection;
  allCollections: SavedBrandCollection[];
  loading?: boolean;
  handleClose: () => void;
  handleConfirm: (options: {
    removeBrands: boolean;
    moveToCollectionId: number | null;
  }) => void;
}

const DeleteCollectionDialog = ({
  open,
  collection,
  allCollections,
  loading,
  handleClose,
  handleConfirm,
}: Props) => {
  const [mode, setMode] = useState<Mode>("keep");
  const [moveToId, setMoveToId] = useState<number>(ALL_SAVED_ID);

  useEffect(() => {
    if (open) {
      setMode("keep");
      setMoveToId(ALL_SAVED_ID);
    }
  }, [open, collection.id]);

  const destinationOptions = useMemo(() => {
    const others = allCollections.filter(
      (c) =>
        c.id !== collection.id &&
        c.id !== ALL_SAVED_ID &&
        !c.isInstagramCollection,
    );
    return [...others.map((c) => ({ id: c.id, name: c.name }))];
  }, [allCollections, collection.id]);

  const totalBrands =
    (collection.unsentCount || 0) + (collection.sentCount || 0);

  const onConfirm = () => {
    handleConfirm({
      removeBrands: mode === "remove",
      moveToCollectionId: mode === "keep" && moveToId > 0 ? moveToId : null,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose}>
      <DialogTitle>Delete &quot;{collection.name}&quot;?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {totalBrands > 0 ? (
            <>
              This collection has <strong>{totalBrands}</strong> saved{" "}
              {totalBrands === 1 ? "brand" : "brands"}. What should happen to{" "}
              {totalBrands === 1 ? "it" : "them"}?
            </>
          ) : (
            <>This collection has no saved brands.</>
          )}
        </Typography>
        {totalBrands > 0 && (
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
          >
            <FormControlLabel
              value="keep"
              control={<Radio />}
              label="Keep the saved brands"
            />
            {mode === "keep" && destinationOptions?.length > 0 && (
              <Box sx={{ pl: 4, pb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, color: "grey.700" }}>
                  Move them to:
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={String(moveToId)}
                    onChange={(e: SelectChangeEvent) =>
                      setMoveToId(Number(e.target.value))
                    }
                  >
                    {destinationOptions.map((opt) => (
                      <MenuItem key={opt.id} value={String(opt.id)}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <FormControlLabel
              value="remove"
              control={<Radio />}
              label="Remove the saved brands"
            />
            {mode === "remove" && (
              <Typography variant="caption" sx={{ pl: 4, color: "error.main" }}>
                These brands will no longer be saved. This cannot be undone.
              </Typography>
            )}
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={onConfirm}
          sx={{ color: "error.main" }}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCollectionDialog;
